import copy from 'copy-to-clipboard';
import { FC, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import {
	Box,
	DotsHorizontalOutline,
	FlagOutline,
	IconButton,
	LinkAltOutline,
	MailOpenOutline,
	PencilAltOutline,
	Popover,
	PopoverContent,
	PopoverTrigger,
	Portal,
	rem,
	StyleProps,
	TrashOutline,
	useDisclosure,
} from '@bonusly/design-system';
import { useBuildRecognizeURL } from '@bonusly/url';
import { FeatureFlag } from '@bonusly/who-am-i';

import { Bonus } from '../../../../../types/bonus';
import { IconOption } from '../../components/common/dropdown/icon-option';
import { BonusFeedTrackedAction, useTrackBonusFeedItemEvent } from '../../hooks/use-bonus-feed-item-analytics';
import { useCreateBonusReport } from '../../hooks/use-bonus-reports';
import { useContextualizedBonusDetails } from '../../hooks/use-contextualized-bonus-details';
import { useToast } from '../../hooks/use-toast';
import { InviteModal } from '../invite';
import { FormState, ReportBonusModal, ReportBonusModalActions } from './report-bonus-modal';

export interface BonusOptionsMenuProps extends StyleProps {
	bonus: Bonus;
	onClickDelete?: VoidFunction;
	onClickEdit?: VoidFunction;
	hideBookmarkButton?: boolean;
}

export const BonusOptionsMenu: FC<BonusOptionsMenuProps> = ({ bonus, onClickDelete, onClickEdit, ...rest }) => {
	const recognizeURL = useBuildRecognizeURL();
	const { t } = useTranslation(['common', 'recognition']);

	const trackCopy = useTrackBonusFeedItemEvent(bonus, BonusFeedTrackedAction.BonusUrlCopy);
	const trackReport = useTrackBonusFeedItemEvent(bonus, BonusFeedTrackedAction.BonusReport);
	const { createSuccessToast } = useToast();
	const reportModalRef = useRef<ReportBonusModalActions>(null);

	const reportModal = useDisclosure();
	const inviteModal = useDisclosure();

	const { isEditableByUser } = useContextualizedBonusDetails(bonus);

	const reportBonus = useCreateBonusReport({
		onSuccess: () => {
			reportModalRef.current?.close();
			createSuccessToast(t('report_bonus.feedback.success', { ns: 'recognition' }));
		},
	});

	const handleCopyUrl = (onCloseMenu: VoidFunction) => () => {
		trackCopy();
		copy(recognizeURL('bonuses', bonus.id));
		createSuccessToast(t('bonus_card.feedback.link_copied_success', { ns: 'recognition' }));
		onCloseMenu();
	};

	const handleClickDelete = (onCloseMenu: VoidFunction) => () => {
		if (onClickDelete) {
			onClickDelete();
		}
		onCloseMenu();
	};

	const handleClickInvite = (onCloseMenu: VoidFunction) => () => {
		inviteModal.onOpen();
		onCloseMenu();
	};

	const handleClickReport = (onCloseMenu: VoidFunction) => () => {
		reportModal.onOpen();
		onCloseMenu();
	};

	const handleConfirmReport = ({ reason }: FormState) => {
		trackReport();

		reportBonus.mutate({
			bonusReport: {
				bonusId: bonus.id,
				reason: 'other',
				userInput: reason,
			},
		});
	};

	const handleClickEdit = (onCloseMenu: VoidFunction) => () => {
		if (onClickEdit) {
			onClickEdit();
		}
		onCloseMenu();
	};

	return (
		<>
			<Popover isLazy returnFocusOnClose={false} placement="bottom-end">
				{({ onClose }) => (
					<>
						<PopoverTrigger>
							<IconButton
								_hover={{ backgroundColor: 'secondary.bgAlpha' }}
								aria-label={t('bonus_card.menu_button_label', {
									ns: 'recognition',
								})}
								backgroundColor="transparent"
								borderRadius="full"
								color="text.default"
								h={rem(44)}
								icon={<DotsHorizontalOutline size="sm" />}
								w={rem(44)}
								{...rest}
							/>
						</PopoverTrigger>

						<Portal>
							<PopoverContent
								aria-label={
									t('bonus_card.menu_popover_label', {
										ns: 'recognition',
									}) ?? ''
								}
								boxShadow="xl"
								border="none"
								width="auto"
								onClick={(e) => e.stopPropagation()}
							>
								<Box
									as="ul"
									role="listbox"
									aria-label={
										t('bonus_card.menu_options_label', {
											ns: 'recognition',
										}) ?? ''
									}
									p={1}
									width={{ base: '100%', sm: rem(200) }}
								>
									<IconOption
										icon={<LinkAltOutline />}
										onClick={handleCopyUrl(onClose)}
										text={t('copy_link', {
											ns: 'common',
										})}
									/>

									<FeatureFlag flag="bonus-invite-others">
										<IconOption
											icon={<MailOpenOutline />}
											onClick={handleClickInvite(onClose)}
											text={t('invite_others', {
												ns: 'recognition',
											})}
										/>
									</FeatureFlag>
									<IconOption
										icon={<FlagOutline />}
										onClick={handleClickReport(onClose)}
										text={t('report', {
											ns: 'common',
										})}
									/>

									{isEditableByUser && onClickEdit && (
										<IconOption
											icon={<PencilAltOutline />}
											onClick={handleClickEdit(onClose)}
											text={t('edit', {
												ns: 'common',
											})}
										/>
									)}

									{isEditableByUser && onClickDelete && (
										<IconOption
											icon={<TrashOutline />}
											onClick={handleClickDelete(onClose)}
											text={t('delete', {
												ns: 'common',
											})}
										/>
									)}
								</Box>
							</PopoverContent>
						</Portal>
					</>
				)}
			</Popover>

			<ReportBonusModal
				isLoading={reportBonus.isLoading}
				isOpen={reportModal.isOpen}
				onClose={reportModal.onClose}
				onConfirm={handleConfirmReport}
				ref={reportModalRef}
			/>

			<InviteModal isOpen={inviteModal.isOpen} onClose={inviteModal.onClose} bonus={bonus} />
		</>
	);
};
